// @ts-nocheck
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Skeleton } from './ui/skeleton';
import { 
  MessageSquare, 
  Lightbulb, 
  Users, 
  ArrowUp, 
  ArrowDown,
  ExternalLink,
  CheckCircle2,
  DollarSign,
  Info
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter, ReferenceLine, ZAxis } from 'recharts';
import { supabase } from '@product-radar/shared';
import { useWorkspace } from '@/contexts/context';

interface DashboardProps {
  onNavigateToIdea?: (ideaId: string) => void;
  onNavigateToFeedback?: (feedbackId: string) => void;
}

interface DashboardMetrics {
  totalFeedback: number;
  totalIdeas: number;
  actionedIdeas: number;
  activeClients: number;
  totalClientValue: number;
}

interface ChartDataPoint {
  name: string;
  feedback: number;
  ideas: number;
}

interface SentimentData {
  name: string;
  value: number;
  color: string;
}

interface TopIdea {
  id: string;
  title: string;
  feedbackCount: number;
  clientValue: number;
  priority: number;
  effort: number;
  status: string;
}

interface RecentFeedback {
  id: string;
  source: string;
  client_name: string;
  content: string;
  sentiment: string;
  created_at: string;
}

interface PrioritizationIdea {
  id: string;
  title: string;
  effort: number;
  impact: number;
  reach: number;
  reachImpact: number;
  confidence: number;
  time_criticality: number;
  risk_reduction: number;
  linkedClients: number;
  clientValue: number;
  riceScore: number;
  valueScore: number;
  wsjfScore: number;
  costOfDelay: number;
}

export function Dashboard({ onNavigateToIdea, onNavigateToFeedback }: DashboardProps) {
  const { workspace, userProfile } = useWorkspace();
  const [timePeriod, setTimePeriod] = useState<'week' | 'month' | '3months'>('week');
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [sentimentData, setSentimentData] = useState<SentimentData[]>([]);
  const [topIdeas, setTopIdeas] = useState<TopIdea[]>([]);
  const [recentFeedback, setRecentFeedback] = useState<RecentFeedback[]>([]);
  const [prioritizationData, setPrioritizationData] = useState<PrioritizationIdea[]>([]);
  const [prioritizationFramework, setPrioritizationFramework] = useState<'rice' | 'value-effort' | 'wsjf'>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('productRadar_prioritizationFramework');
      if (stored === 'rice' || stored === 'value-effort' || stored === 'wsjf') return stored;
    }
    return 'rice';
  });
  const [impactEffortYAxis, setImpactEffortYAxis] = useState<'impact' | 'arr'>('impact');
  const [impactEffortLimit, setImpactEffortLimit] = useState<10 | 20 | 50>(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workspace) {
      loadDashboardData();
    }
  }, [workspace, timePeriod]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('productRadar_prioritizationFramework', prioritizationFramework);
    }
  }, [prioritizationFramework]);

  const getDaysAgo = () => {
    switch (timePeriod) {
      case 'week': return 7;
      case 'month': return 30;
      case '3months': return 90;
      default: return 7;
    }
  };

  const loadDashboardData = async () => {
    if (!workspace) return;
    
    setLoading(true);
    try {
      await Promise.all([
        loadMetrics(),
        loadChartData(),
        loadSentimentData(),
        loadTopIdeas(),
        loadRecentFeedback(),
        loadPrioritizationData()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    if (!workspace) return;

    const daysAgo = getDaysAgo();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

    // Get total feedback (exclude Archived - matches list view default)
    const { count: feedbackCount } = await supabase
      .from('feedback')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspace.id)
      .neq('status', 'Archived');

    // Get total ideas (exclude Archived and Merged - matches list view default)
    const { count: ideasCount } = await supabase
      .from('ideas')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspace.id)
      .not('status', 'in', '("Archived","Merged")');

    // Get actioned ideas (submitted to integrations or completed)
    const { count: actionedCount } = await supabase
      .from('ideas')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspace.id)
      .in('status', ['In Development', 'Shipped']);

    // Get active clients (status is lowercase: 'active', 'inactive', 'churned')
    const { count: clientsCount } = await supabase
      .from('clients')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspace.id)
      .eq('status', 'active');

    // Get total client value (sum of ARR)
    const { data: clientsData } = await supabase
      .from('clients')
      .select('arr')
      .eq('workspace_id', workspace.id)
      .eq('status', 'active');

    const totalValue = clientsData?.reduce((sum, client) => sum + (client.arr || 0), 0) || 0;

    setMetrics({
      totalFeedback: feedbackCount || 0,
      totalIdeas: ideasCount || 0,
      actionedIdeas: actionedCount || 0,
      activeClients: clientsCount || 0,
      totalClientValue: totalValue
    });
  };

  const loadChartData = async () => {
    if (!workspace) return;

    const daysAgo = getDaysAgo();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

    // Get feedback and ideas created in the time period (exclude Archived/Merged)
    const { data: feedbackData } = await supabase
      .from('feedback')
      .select('created_at')
      .eq('workspace_id', workspace.id)
      .neq('status', 'Archived')
      .gte('created_at', cutoffDate.toISOString());

    const { data: ideasData } = await supabase
      .from('ideas')
      .select('created_at')
      .eq('workspace_id', workspace.id)
      .not('status', 'in', '("Archived","Merged")')
      .gte('created_at', cutoffDate.toISOString());

    // Group by time period
    const grouped = groupByTimePeriod(feedbackData || [], ideasData || [], timePeriod);
    setChartData(grouped);
  };

  const groupByTimePeriod = (feedback: any[], ideas: any[], period: string): ChartDataPoint[] => {
    if (period === 'week') {
      // Group by day of week
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const counts = days.map(() => ({ feedback: 0, ideas: 0 }));
      
      feedback.forEach(item => {
        const day = new Date(item.created_at).getDay();
        counts[day].feedback++;
      });
      
      ideas.forEach(item => {
        const day = new Date(item.created_at).getDay();
        counts[day].ideas++;
      });
      
      return days.map((name, idx) => ({
        name,
        feedback: counts[idx].feedback,
        ideas: counts[idx].ideas
      }));
    } else if (period === 'month') {
      // Group by week
      const weeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
      const counts = weeks.map(() => ({ feedback: 0, ideas: 0 }));
      
      feedback.forEach(item => {
        const weekNum = Math.floor((30 - Math.floor((Date.now() - new Date(item.created_at).getTime()) / (1000 * 60 * 60 * 24))) / 7);
        if (weekNum >= 0 && weekNum < 4) counts[weekNum].feedback++;
      });
      
      ideas.forEach(item => {
        const weekNum = Math.floor((30 - Math.floor((Date.now() - new Date(item.created_at).getTime()) / (1000 * 60 * 60 * 24))) / 7);
        if (weekNum >= 0 && weekNum < 4) counts[weekNum].ideas++;
      });
      
      return weeks.map((name, idx) => ({
        name,
        feedback: counts[idx].feedback,
        ideas: counts[idx].ideas
      }));
    } else {
      // Group by month
      const months = ['Month 1', 'Month 2', 'Month 3'];
      const counts = months.map(() => ({ feedback: 0, ideas: 0 }));
      
      feedback.forEach(item => {
        const monthNum = Math.floor((90 - Math.floor((Date.now() - new Date(item.created_at).getTime()) / (1000 * 60 * 60 * 24))) / 30);
        if (monthNum >= 0 && monthNum < 3) counts[monthNum].feedback++;
      });
      
      ideas.forEach(item => {
        const monthNum = Math.floor((90 - Math.floor((Date.now() - new Date(item.created_at).getTime()) / (1000 * 60 * 60 * 24))) / 30);
        if (monthNum >= 0 && monthNum < 3) counts[monthNum].ideas++;
      });
      
      return months.map((name, idx) => ({
        name,
        feedback: counts[idx].feedback,
        ideas: counts[idx].ideas
      }));
    }
  };

  const loadSentimentData = async () => {
    if (!workspace) return;

    const { data } = await supabase
      .from('feedback')
      .select('sentiment')
      .eq('workspace_id', workspace.id)
      .neq('status', 'Archived');

    if (!data || data.length === 0) {
      setSentimentData([
        { name: 'Positive', value: 0, color: '#10b981' },
        { name: 'Neutral', value: 0, color: '#f59e0b' },
        { name: 'Negative', value: 0, color: '#ef4444' },
      ]);
      return;
    }

    const total = data.length;
    const positive = data.filter(f => f.sentiment === 'positive').length;
    const neutral = data.filter(f => f.sentiment === 'neutral').length;
    const negative = data.filter(f => f.sentiment === 'negative').length;

    setSentimentData([
      { name: 'Positive', value: Math.round((positive / total) * 100), color: '#10b981' },
      { name: 'Neutral', value: Math.round((neutral / total) * 100), color: '#f59e0b' },
      { name: 'Negative', value: Math.round((negative / total) * 100), color: '#ef4444' },
    ]);
  };

  const loadTopIdeas = async () => {
    if (!workspace) return;

    // Get ideas with feedback count and client value (exclude Archived and Merged)
    const { data: ideas } = await supabase
      .from('ideas')
      .select(`
        id,
        title,
        priority,
        effort,
        status
      `)
      .eq('workspace_id', workspace.id)
      .not('status', 'in', '("Archived","Merged")')
      .order('priority', { ascending: false })
      .limit(4);

    if (!ideas) {
      setTopIdeas([]);
      return;
    }

    // For each idea, get feedback count and client value
    const ideasWithMetrics = await Promise.all(
      ideas.map(async (idea) => {
        // Get feedback count
        const { count: feedbackCount } = await supabase
          .from('feedback_ideas')
          .select('*', { count: 'exact', head: true })
          .eq('idea_id', idea.id);

        // Get client value through client_ideas junction
        const { data: clientLinks } = await supabase
          .from('client_ideas')
          .select('client_id')
          .eq('idea_id', idea.id);

        let clientValue = 0;
        if (clientLinks && clientLinks.length > 0) {
          const clientIds = clientLinks.map(link => link.client_id);
          const { data: clients } = await supabase
            .from('clients')
            .select('arr')
            .in('id', clientIds);

          clientValue = clients?.reduce((sum, client) => sum + (client.arr || 0), 0) || 0;
        }

        return {
          ...idea,
          feedbackCount: feedbackCount || 0,
          clientValue
        };
      })
    );

    setTopIdeas(ideasWithMetrics);
  };

  const loadPrioritizationData = async () => {
    if (!workspace) return;

    const { data: ideas } = await supabase
      .from('ideas')
      .select(`
        id,
        title,
        effort,
        impact,
        reach,
        confidence,
        time_criticality,
        risk_reduction
      `)
      .eq('workspace_id', workspace.id)
      .not('status', 'in', '("Archived","Merged")');

    if (!ideas || ideas.length === 0) {
      setPrioritizationData([]);
      return;
    }

    const ideasWithMetrics = await Promise.all(
      ideas.map(async (idea) => {
        const { count: feedbackCount } = await supabase
          .from('feedback_ideas')
          .select('*', { count: 'exact', head: true })
          .eq('idea_id', idea.id);

        const { data: clientLinks } = await supabase
          .from('client_ideas')
          .select('client_id')
          .eq('idea_id', idea.id);

        let clientValue = 0;
        let linkedClients = 0;
        if (clientLinks && clientLinks.length > 0) {
          linkedClients = clientLinks.length;
          const clientIds = clientLinks.map((link: { client_id: string }) => link.client_id);
          const { data: clients } = await supabase
            .from('clients')
            .select('arr')
            .in('id', clientIds);
          clientValue = clients?.reduce((sum: number, client: { arr?: number }) => sum + (client.arr || 0), 0) || 0;
        }

        const effectiveReach = idea.reach ?? linkedClients;
        const eff = idea.effort || 1;
        const imp = idea.impact || 3;
        const conf = (idea.confidence ?? 70) / 100;
        const tc = idea.time_criticality ?? 5;
        const rr = idea.risk_reduction ?? 5;

        const reachImpact = effectiveReach * imp;
        const riceScore = (effectiveReach * imp * conf) / eff;
        const rawValue = imp * 2 + (clientValue / 1000) + linkedClients;
        const businessValue = imp * 2;
        const costOfDelay = businessValue + tc + rr;
        const wsjfScore = costOfDelay / eff;

        return {
          id: idea.id,
          title: idea.title,
          effort: eff,
          impact: imp,
          reach: effectiveReach,
          reachImpact,
          confidence: (idea.confidence ?? 70),
          time_criticality: tc,
          risk_reduction: rr,
          linkedClients,
          clientValue,
          riceScore,
          valueScore: rawValue,
          wsjfScore,
          costOfDelay
        };
      })
    );

    const maxRaw = Math.max(...ideasWithMetrics.map((i) => i.valueScore), 1);
    const minRaw = Math.min(...ideasWithMetrics.map((i) => i.valueScore), 0);
    const range = maxRaw - minRaw || 1;
    ideasWithMetrics.forEach((i) => {
      i.valueScore = Math.min(10, Math.max(1, 1 + ((i.valueScore - minRaw) / range) * 9));
    });

    setPrioritizationData(ideasWithMetrics);
  };

  const loadRecentFeedback = async () => {
    if (!workspace) return;

    const { data } = await supabase
      .from('feedback')
      .select(`
        id,
        source,
        content,
        sentiment,
        created_at,
        client:clients(name)
      `)
      .eq('workspace_id', workspace.id)
      .neq('status', 'Archived')
      .order('created_at', { ascending: false })
      .limit(4);

    if (data) {
      setRecentFeedback(data.map(f => ({
        ...f,
        client_name: f.client?.name || 'Unknown'
      })));
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    } else {
      return `$${value}`;
    }
  };

  const getEffortLabel = (effort: number) => {
    switch (effort) {
      case 1: return 'XS';
      case 2: return 'S';
      case 3: return 'M';
      case 4: return 'L';
      case 5: return 'XL';
      default: return 'M';
    }
  };

  const getPriorityVariant = (priority: number) => {
    if (priority >= 4) return 'destructive';
    if (priority >= 3) return 'secondary';
    return 'outline';
  };

  const getPriorityLabel = (priority: number) => {
    if (priority >= 4) return 'High';
    if (priority >= 3) return 'Medium';
    return 'Low';
  };

  const getTimeAgo = (date: string) => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-display-sm font-semibold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Loading your dashboard...</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardHeader className="space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-display-sm font-semibold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome back, {userProfile?.full_name || 'there'}. Here's what's happening with your feedback.
          </p>
        </div>
        <div className="flex space-x-3">
          <Button size="sm" variant="outline">
            <ExternalLink className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Time Period Selector */}
      <div className="flex items-center justify-end">
        <Tabs value={timePeriod} onValueChange={(value) => setTimePeriod(value as 'week' | 'month' | '3months')}>
          <TabsList>
            <TabsTrigger value="week">Week</TabsTrigger>
            <TabsTrigger value="month">Month</TabsTrigger>
            <TabsTrigger value="3months">3 Months</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Feedback</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.totalFeedback.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">All-time feedback items</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Ideas</CardTitle>
            <Lightbulb className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.totalIdeas.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Generated from feedback</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actioned Ideas</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.actionedIdeas.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">In progress or completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Clients</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.activeClients.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatCurrency(metrics?.totalClientValue || 0)} total ARR
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Prioritization Framework Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Prioritization</CardTitle>
              <CardDescription className="flex flex-wrap items-center gap-x-1">
                View ideas through{' '}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-0.5 cursor-help underline-offset-2 hover:underline">
                      RICE <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs py-2 px-3">
                    <p className="font-medium">Reach × Impact × Confidence ÷ Effort</p>
                    <p className="text-xs opacity-90 mt-0.5">Scores ideas by potential reach, impact, confidence level, and effort. Top-right = high reach, impact and confidence, with low effort = best opportunistic ideas.</p>
                  </TooltipContent>
                </Tooltip>
                ,{' '}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-0.5 cursor-help underline-offset-2 hover:underline">
                      Impact vs Effort <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs py-2 px-3">
                    <p className="font-medium">Impact or ARR vs Effort</p>
                    <p className="text-xs opacity-90 mt-0.5">Plots ideas by impact (or client ARR) vs effort. Top-right = high value, low effort = best opportunities.</p>
                  </TooltipContent>
                </Tooltip>
                , and{' '}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-0.5 cursor-help underline-offset-2 hover:underline">
                      WSJF <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs py-2 px-3">
                    <p className="font-medium">Weighted Shortest Job First</p>
                    <p className="text-xs opacity-90 mt-0.5">(Business Value + Time Criticality + Risk Reduction) ÷ Job Size. Top Right = high-value, urgent work with lowest effort.</p>
                  </TooltipContent>
                </Tooltip>
                {' '}frameworks
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <Tabs value={prioritizationFramework} onValueChange={(v) => setPrioritizationFramework(v as 'rice' | 'value-effort' | 'wsjf')}>
                <TabsList>
                  <TabsTrigger value="rice">RICE</TabsTrigger>
                  <TabsTrigger value="value-effort">Impact vs Effort</TabsTrigger>
                  <TabsTrigger value="wsjf">WSJF</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Show</Label>
                <Select value={String(impactEffortLimit)} onValueChange={(v) => setImpactEffortLimit(Number(v) as 10 | 20 | 50)}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">Top 10</SelectItem>
                    <SelectItem value="20">Top 20</SelectItem>
                    <SelectItem value="50">Top 50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {prioritizationData.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">
              No ideas to prioritize. Create ideas and add reach, confidence, and other prioritization fields.
            </div>
          ) : prioritizationFramework === 'rice' ? (
            (() => {
              const sorted = [...prioritizationData].sort((a, b) => (b.riceScore ?? 0) - (a.riceScore ?? 0));
              const limited = sorted.slice(0, impactEffortLimit);
              const riceData = limited.map((i) => ({
                ...i,
                reachImpactConfidence: i.reachImpact * ((i.confidence ?? 70) / 100)
              }));
              const riceMin = riceData.length ? Math.min(...riceData.map((i) => i.riceScore)) : 0;
              const riceMax = riceData.length ? Math.max(...riceData.map((i) => i.riceScore)) : 1;
              const riceRange = riceMax - riceMin || 1;
              const COLOURS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#10b981'];
              const getColour = (d: PrioritizationIdea) => {
                const t = (d.riceScore - riceMin) / riceRange;
                const idx = Math.min(Math.floor(t * COLOURS.length), COLOURS.length - 1);
                return COLOURS[Math.max(0, idx)];
              };
              return (
                <ResponsiveContainer width="100%" height={350}>
                  <ScatterChart margin={{ top: 24, right: 24, bottom: 40, left: 64 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      type="number"
                      dataKey="effort"
                      name="Effort"
                      domain={[0.5, 5.5]}
                      ticks={[1, 2, 3, 4, 5]}
                      reversed
                      label={{ value: 'Effort (high ← → low)', position: 'bottom', offset: 20 }}
                    />
                    <YAxis
                      type="number"
                      dataKey="reachImpactConfidence"
                      name="Reach × Impact × Confidence"
                      label={{ value: 'Reach × Impact × Confidence', angle: -90, position: 'center', offset: -36, style: { fontSize: 11 } }}
                    />
                    <ZAxis type="number" dataKey="confidence" range={[100, 1000]} name="Confidence" />
                    <RechartsTooltip
                      cursor={{ strokeDasharray: '3 3', stroke: '#94a3b8' }}
                      content={({ active, payload }) => {
                        if (active && payload?.length) {
                          const p = payload[0]?.payload as PrioritizationIdea & { reachImpactConfidence?: number };
                          const reach = p.reach ?? 0;
                          const impact = p.impact ?? 3;
                          const conf = p.confidence ?? 70;
                          const eff = p.effort ?? 1;
                          const formula = `(Reach(${reach}) × Impact(${impact}) × Confidence(${conf}%)) ÷ Effort(${eff}) = ${p.riceScore?.toFixed(1)}`;
                          return (
                            <div className="rounded-lg border bg-background p-3 shadow-md">
                              <p className="font-medium">{p?.title}</p>
                              <p className="text-sm text-muted-foreground">RICE: {formula}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter data={riceData} name="Ideas" onClick={(e) => e?.payload?.id && onNavigateToIdea?.(e.payload.id)}>
                      {riceData.map((entry) => (
                        <Cell key={entry.id} fill={getColour(entry)} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              );
            })()
          ) : prioritizationFramework === 'value-effort' ? (
            (() => {
              const sorted = [...prioritizationData].sort((a, b) => (b.valueScore ?? 0) - (a.valueScore ?? 0));
              const limited = sorted.slice(0, impactEffortLimit);
              const maxArr = Math.max(...limited.map((i) => i.clientValue), 1);
              const bubbleData = limited.map((i) => ({
                ...i,
                yValue: impactEffortYAxis === 'impact' ? (i.impact ?? 3) : i.clientValue,
                bubbleSize: 60 + (i.clientValue / maxArr) * 240
              }));
              const maxY = impactEffortYAxis === 'impact' ? 5.5 : Math.max(...bubbleData.map((i) => i.yValue), 1) * 1.1;
              const minY = impactEffortYAxis === 'impact' ? 0.5 : 0;

              // Add jitter so overlapping points (same effort, yValue) are all visible
              const coordKey = (d: (typeof bubbleData)[0]) => `${d.effort},${d.yValue}`;
              const groups = new Map<string, (typeof bubbleData)[0][]>();
              for (const d of bubbleData) {
                const k = coordKey(d);
                if (!groups.has(k)) groups.set(k, []);
                groups.get(k)!.push(d);
              }
              const yRange = maxY - minY || 1;
              const effortJitter = 0.14;
              const yJitter = impactEffortYAxis === 'impact' ? 0.14 : yRange * 0.02;
              bubbleData.forEach((d) => {
                const group = groups.get(coordKey(d))!;
                const idx = group.indexOf(d);
                const n = group.length;
                const angle = n > 1 ? (idx / n) * 2 * Math.PI : 0;
                const r = n > 1 ? (0.5 + idx * 0.15) : 0;
                const jx = r ? r * Math.cos(angle) * effortJitter : 0;
                const jy = r ? r * Math.sin(angle) * yJitter : 0;
                (d as any).effortDisplay = d.effort + jx;
                (d as any).yValueDisplay = d.yValue + jy;
              });
              const ratios = bubbleData.map((i) => i.impact / Math.max(i.effort, 0.5));
              const scoreMin = ratios.length ? Math.min(...ratios) : 0;
              const scoreMax = ratios.length ? Math.max(...ratios) : 1;
              const scoreRange = scoreMax - scoreMin || 1;
              const COLOURS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#10b981'];
              const getColour = (d: (typeof bubbleData)[0]) => {
                const score = d.impact / Math.max(d.effort, 0.5);
                const t = (score - scoreMin) / scoreRange;
                const idx = Math.min(Math.floor(t * COLOURS.length), COLOURS.length - 1);
                return COLOURS[Math.max(0, idx)];
              };
              return (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-4">
                    <Label className="text-sm">Y-axis</Label>
                    <Select value={impactEffortYAxis} onValueChange={(v) => setImpactEffortYAxis(v as 'impact' | 'arr')}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="impact">Impact (1–5)</SelectItem>
                        <SelectItem value="arr">ARR value</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <ResponsiveContainer width="100%" height={350}>
                    <ScatterChart margin={{ top: 24, right: 24, bottom: 40, left: 44 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        type="number"
                        dataKey="effortDisplay"
                        name="Effort"
                        domain={[0.5, 5.5]}
                        ticks={[1, 2, 3, 4, 5]}
                        tick={{ fontSize: 11 }}
                        reversed
                        label={{ value: 'Effort (high ← → low)', position: 'bottom', offset: 20 }}
                      />
                      <YAxis
                        type="number"
                        dataKey="yValueDisplay"
                        name={impactEffortYAxis === 'impact' ? 'Impact' : 'ARR'}
                        domain={[minY - yJitter * 1.5, maxY + yJitter * 1.5]}
                        tick={{ fontSize: 11 }}
                        tickFormatter={impactEffortYAxis === 'arr' ? (v) => formatCurrency(v) : undefined}
                        label={{
                          value: impactEffortYAxis === 'impact' ? 'Impact' : 'ARR',
                          angle: -90,
                          position: 'center',
                          offset: -24
                        }}
                      />
                      <ZAxis type="number" dataKey="bubbleSize" range={[60, 300]} />
                      <RechartsTooltip
                        cursor={{ strokeDasharray: '3 3', stroke: '#94a3b8' }}
                        content={({ active, payload }) => {
                          if (active && payload?.length) {
                            const p = payload[0]?.payload as PrioritizationIdea & { yValue: number };
                            return (
                              <div className="rounded-lg border bg-background p-3 shadow-md">
                                <p className="font-medium">{p?.title}</p>
                                <p className="text-sm text-muted-foreground">Effort: {p?.effort} · Impact: {p?.impact}</p>
                                <p className="text-sm text-muted-foreground">{formatCurrency(p?.clientValue || 0)} client ARR</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Scatter data={bubbleData} name="Ideas" onClick={(e) => e?.payload?.id && onNavigateToIdea?.(e.payload.id)}>
                        {bubbleData.map((entry) => (
                          <Cell key={entry.id} fill={getColour(entry)} stroke="#fff" strokeWidth={2} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              );
            })()
          ) : (
            (() => {
              const sorted = [...prioritizationData].sort((a, b) => (b.wsjfScore ?? 0) - (a.wsjfScore ?? 0));
              const limited = sorted.slice(0, impactEffortLimit);
              const maxWsjf = Math.max(...limited.map((i) => i.wsjfScore), 0.1);
              const minCod = Math.min(...limited.map((i) => i.costOfDelay), 0);
              const maxCod = Math.max(...limited.map((i) => i.costOfDelay), 1);
              const wsjfData = limited.map((i) => ({
                ...i,
                bubbleSize: 60 + (i.wsjfScore / maxWsjf) * 240
              }));
              const coordKey = (d: (typeof wsjfData)[0]) => `${d.effort},${d.costOfDelay}`;
              const groups = new Map<string, (typeof wsjfData)[0][]>();
              for (const d of wsjfData) {
                const k = coordKey(d);
                if (!groups.has(k)) groups.set(k, []);
                groups.get(k)!.push(d);
              }
              const effortJitter = 0.14;
              const codJitter = (maxCod - minCod || 1) * 0.02;
              wsjfData.forEach((d) => {
                const group = groups.get(coordKey(d))!;
                const idx = group.indexOf(d);
                const n = group.length;
                const angle = n > 1 ? (idx / n) * 2 * Math.PI : 0;
                const r = n > 1 ? (0.5 + idx * 0.15) : 0;
                const jx = r ? r * Math.cos(angle) * effortJitter : 0;
                const jy = r ? r * Math.sin(angle) * codJitter : 0;
                (d as any).effortDisplay = d.effort + jx;
                (d as any).codDisplay = d.costOfDelay + jy;
              });
              const scoreMin = wsjfData.length ? Math.min(...wsjfData.map((i) => i.wsjfScore)) : 0;
              const scoreMax = wsjfData.length ? Math.max(...wsjfData.map((i) => i.wsjfScore)) : 1;
              const scoreRange = scoreMax - scoreMin || 1;
              const COLOURS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#10b981'];
              const getColour = (d: (typeof wsjfData)[0]) => {
                const t = (d.wsjfScore - scoreMin) / scoreRange;
                const idx = Math.min(Math.floor(t * COLOURS.length), COLOURS.length - 1);
                return COLOURS[Math.max(0, idx)];
              };
              return (
                <ResponsiveContainer width="100%" height={350}>
                  <ScatterChart margin={{ top: 24, right: 24, bottom: 40, left: 44 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      type="number"
                      dataKey="effortDisplay"
                      name="Job Size"
                      domain={[0.5, 5.5]}
                      ticks={[1, 2, 3, 4, 5]}
                      tick={{ fontSize: 11 }}
                      reversed
                      label={{ value: 'Job Size (effort, high ← → low)', position: 'bottom', offset: 20 }}
                    />
                    <YAxis
                      type="number"
                      dataKey="codDisplay"
                      name="Cost of Delay"
                      domain={[minCod - codJitter * 1.5, maxCod + codJitter * 1.5]}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => Number(v).toFixed(1).replace(/\.0$/, '')}
                      label={{
                        value: 'Cost of Delay (CoD)',
                        angle: -90,
                        position: 'center',
                        offset: -24
                      }}
                    />
                    <ZAxis type="number" dataKey="bubbleSize" range={[60, 300]} name="WSJF score" />
                    <RechartsTooltip
                      cursor={{ strokeDasharray: '3 3', stroke: '#94a3b8' }}
                      content={({ active, payload }) => {
                        if (active && payload?.length) {
                          const p = payload[0]?.payload as PrioritizationIdea;
                          const bv = (p.impact ?? 3) * 2;
                          const tc = p.time_criticality ?? 5;
                          const rr = p.risk_reduction ?? 5;
                          const js = p.effort ?? 1;
                          const formula = `(Business Value(${bv}) + Time Criticality(${tc}) + Risk Reduction(${rr})) ÷ Job Size(${js}) = ${p.wsjfScore?.toFixed(1)}`;
                          return (
                            <div className="rounded-lg border bg-background p-3 shadow-md">
                              <p className="font-medium">{p?.title}</p>
                              <p className="text-sm text-muted-foreground">WSJF: {formula}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter data={wsjfData} name="Ideas" onClick={(e) => e?.payload?.id && onNavigateToIdea?.(e.payload.id)}>
                      {wsjfData.map((entry) => (
                        <Cell key={entry.id} fill={getColour(entry)} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              );
            })()
          )}
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Feedback & Ideas Trend</CardTitle>
            <CardDescription>
              {timePeriod === 'week' ? 'Daily' : timePeriod === 'month' ? 'Weekly' : 'Monthly'} feedback received and ideas generated
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 40, left: 44 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" label={{ value: 'Period', position: 'bottom', offset: 20 }} />
                <YAxis label={{ value: 'Count', angle: -90, position: 'center', offset: -24 }} />
                <RechartsTooltip />
                <Bar dataKey="feedback" fill="#3b82f6" name="Feedback" radius={[4, 4, 0, 0]} />
                <Bar dataKey="ideas" fill="#8b5cf6" name="Ideas" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sentiment Analysis</CardTitle>
            <CardDescription>Breakdown of feedback sentiment</CardDescription>
          </CardHeader>
          <CardContent>
            {sentimentData.reduce((sum, d) => sum + d.value, 0) === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No sentiment data available
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={sentimentData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => value > 0 ? `${value}%` : ''}
                      labelLine={false}
                    >
                      {sentimentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => `${value}%`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center space-x-4 mt-4">
                  {sentimentData.map((item) => (
                    <div key={item.name} className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-sm font-medium">{item.name}: {item.value}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top Priority Ideas</CardTitle>
            <CardDescription>Ideas with highest priority and client value</CardDescription>
          </CardHeader>
          <CardContent>
            {topIdeas.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No ideas yet. Create your first idea!
              </div>
            ) : (
              <div className="space-y-4">
                {topIdeas.map((idea) => (
                  <div key={idea.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{idea.title}</span>
                        <Badge variant={getPriorityVariant(idea.priority)}>
                          {getPriorityLabel(idea.priority)}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                        <span>{idea.feedbackCount} feedback items</span>
                        <span>{formatCurrency(idea.clientValue)} client value</span>
                        <Badge variant="outline" className="text-xs">
                          {getEffortLabel(idea.effort)}
                        </Badge>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => onNavigateToIdea?.(idea.id)}
                    >
                      View
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Feedback</CardTitle>
            <CardDescription>Latest customer feedback from all sources</CardDescription>
          </CardHeader>
          <CardContent>
            {recentFeedback.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No feedback yet. Add your first feedback item!
              </div>
            ) : (
              <div className="space-y-4">
                {recentFeedback.map((feedback) => (
                  <div 
                    key={feedback.id} 
                    className="flex items-start space-x-3 p-3 border border-border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => onNavigateToFeedback?.(feedback.id)}
                  >
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      feedback.sentiment === 'positive' ? 'bg-green-500' :
                      feedback.sentiment === 'negative' ? 'bg-red-500' : 'bg-yellow-500'
                    }`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium">{feedback.client_name}</span>
                          <Badge variant="outline" className="text-xs">{feedback.source}</Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">{getTimeAgo(feedback.created_at)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 truncate">{feedback.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}